import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios'
import { delay, isRetryableNetworkError } from './functions/networkError.utils'

const MAX_NETWORK_RETRIES = 2
const BASE_RETRY_DELAY_MS = 300

export type NetworkRetryConfig = InternalAxiosRequestConfig & {
  _networkRetryCount?: number
}

/**
 * Retries idempotent requests once/twice on transient Axios network errors
 * (RUM issue 004 — AxiosError: Network Error burst pattern).
 */
export const attachNetworkRetryInterceptor = (
  instance: AxiosInstance
): void => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as NetworkRetryConfig | undefined
      if (!config) {
        return Promise.reject(error)
      }

      const retryCount = config._networkRetryCount ?? 0
      const online =
        typeof navigator === 'undefined' || navigator.onLine !== false

      if (
        online &&
        isRetryableNetworkError(error) &&
        retryCount < MAX_NETWORK_RETRIES
      ) {
        config._networkRetryCount = retryCount + 1
        await delay(BASE_RETRY_DELAY_MS * 2 ** retryCount)
        return instance.request(config)
      }

      return Promise.reject(error)
    }
  )
}

const axiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

attachNetworkRetryInterceptor(axiosInstance)

export default axiosInstance
